import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, ArrowRight, CheckCircle2, ChevronDown, Phone, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteModalContext = createContext({ open: () => {}, close: () => {} });

export const useQuoteModal = () => useContext(QuoteModalContext);

export const QuoteModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const open = useCallback(() => {
    setSubmitted(false);
    setError('');
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: (data.get('name') || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
      website: (data.get('website') || '').toString().trim(),
      service: (data.get('service') || '').toString(),
      description: (data.get('description') || '').toString().trim(),
      source: 'quote-modal',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
    };
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setError("Sorry — we couldn't send your request. Please call 1800 006 296 or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <QuoteModalContext.Provider value={{ open, close }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-200"
          data-testid="quote-modal"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
            onClick={close}
          />

          {/* Modal */}
          <div className="relative w-full max-w-md md:max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <button
              onClick={close}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:text-slate-900 transition-all"
              data-testid="quote-modal-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Gradient header */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-7 pt-10 pb-8 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-[#15803d]/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-[#db2777]/30 blur-3xl" />
              <div className="relative">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-[#15803d] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                  FREE Quote
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                  Get Your FREE Quote
                </h2>
                <p className="text-sm text-slate-300">
                  One of our digital strategists will be in touch within 24 hours.
                </p>
                <a
                  href="tel:1800006296"
                  className="mt-3 inline-flex items-center gap-1.5 text-[#86efac] hover:text-white text-sm font-semibold transition-colors"
                >
                  <Phone size={14} /> 1800 006 296
                </a>
              </div>
            </div>

            <div className="p-7 md:p-8">
              {submitted ? (
                <div className="py-10 text-center" data-testid="quote-modal-success">
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#15803d]/10 flex items-center justify-center text-[#15803d] mb-4">
                    <CheckCircle2 size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">Thanks! We've got your details.</h4>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    One of our digital strategists will be in touch shortly.
                  </p>
                  <button
                    onClick={close}
                    className="rounded-full bg-slate-900 text-white px-6 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form className="space-y-3.5" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      data-testid="quote-modal-name"
                      className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@company.com"
                        data-testid="quote-modal-email"
                        className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="04xx xxx xxx"
                        data-testid="quote-modal-phone"
                        className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                      Website
                    </label>
                    <input
                      type="text"
                      name="website"
                      placeholder="www.yourwebsite.com"
                      data-testid="quote-modal-website"
                      className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                      Service of Interest *
                    </label>
                    <div className="relative">
                      <label htmlFor="quote-modal-service" className="sr-only">
                        Service of interest
                      </label>
                      <select
                        id="quote-modal-service"
                        aria-label="Service of interest"
                        required
                        name="service"
                        defaultValue=""
                        data-testid="quote-modal-service"
                        className="w-full appearance-none rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 pr-10 text-slate-900 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all cursor-pointer"
                      >
                        <option value="" disabled>Select a service...</option>
                        <option value="seo">SEO</option>
                        <option value="google-ads">Google Ads</option>
                        <option value="web-design">Web Design</option>
                        <option value="link-building">Link Building</option>
                        <option value="content-marketing">Content Marketing</option>
                        <option value="local-seo">Local SEO</option>
                        <option value="ecommerce-seo">eCommerce SEO</option>
                        <option value="google-penalty-removal">Google Penalty Removal</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown
                        size={18}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                      Tell us about your project
                    </label>
                    <textarea
                      rows={3}
                      name="description"
                      placeholder="What are your goals? Any target keywords or markets?"
                      data-testid="quote-modal-description"
                      className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-modal-submit"
                    className="group w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#db2777] to-[#be185d] hover:from-[#be185d] hover:to-[#9f1650] disabled:opacity-70 disabled:cursor-not-allowed text-white py-4 font-bold text-base tracking-wide shadow-lg shadow-[#db2777]/30 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 rounded-full border-2 border-white/50 border-t-white animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        GET MY FREE QUOTE
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  {error && (
                    <p
                      role="alert"
                      data-testid="quote-modal-error"
                      className="flex items-start gap-2 text-[12px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                    >
                      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </p>
                  )}

                  <p className="text-[11px] text-slate-500 text-center leading-relaxed pt-1">
                    No lock-in contracts. 100% Aussie team.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </QuoteModalContext.Provider>
  );
};

/** Simple button that triggers the global quote modal. */
export const QuoteButton = ({ children, className = '', 'data-testid': testId, ...rest }) => {
  const { open } = useQuoteModal();
  return (
    <button type="button" onClick={open} className={className} data-testid={testId} {...rest}>
      {children}
    </button>
  );
};

export default QuoteModalProvider;
